import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ActivateReferralProgramResponse,
  BulkGetReferralProgramResponse,
  GenerateAISocialMediaPostsSuggestionsResponse,
  GenerateAiSocialMediaPostsSuggestionsOptions,
  GetAISocialMediaPostsSuggestionsResponse,
  GetAiSocialMediaPostsSuggestionsOptions,
  GetReferralProgramPremiumFeaturesResponse,
  GetReferralProgramResponse,
  PauseReferralProgramResponse,
  ProgramUpdatedEnvelope,
  ReferralProgram,
  UpdateReferralProgramResponse,
  activateReferralProgram as universalActivateReferralProgram,
  bulkGetReferralProgram as universalBulkGetReferralProgram,
  generateAiSocialMediaPostsSuggestions as universalGenerateAiSocialMediaPostsSuggestions,
  getAiSocialMediaPostsSuggestions as universalGetAiSocialMediaPostsSuggestions,
  getReferralProgram as universalGetReferralProgram,
  getReferralProgramPremiumFeatures as universalGetReferralProgramPremiumFeatures,
  pauseReferralProgram as universalPauseReferralProgram,
  updateReferralProgram as universalUpdateReferralProgram,
} from './loyalty-referral-v1-program-programs.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/referral' };

export function getReferralProgram(
  httpClient: HttpClient
): GetReferralProgramSignature {
  return () =>
    universalGetReferralProgram(
      // @ts-ignore
      { httpClient }
    );
}

interface GetReferralProgramSignature {
  /**
   * Retrieves the referral program.
   */
  (): Promise<
    NonNullablePaths<
      GetReferralProgramResponse,
      | `referralProgram.status`
      | `referralProgram.referredFriendReward.couponOptions.fixedAmountOptions.amount`
      | `referralProgram.referredFriendReward.couponOptions.percentageOptions.percentage`
      | `referralProgram.referredFriendReward.couponOptions.minimumSubtotal`
      | `referralProgram.referredFriendReward.couponOptions.scope.namespace`
      | `referralProgram.referredFriendReward.couponOptions.scope.group.name`
      | `referralProgram.referredFriendReward.couponOptions.name`
      | `referralProgram.referredFriendReward.couponOptions.discountType`
      | `referralProgram.referredFriendReward.loyaltyPointsOptions.amount`
      | `referralProgram.referredFriendReward.type`
      | `referralProgram.successfulReferralActions`
      | `referralProgram.emails.encourageToReferFriends`
      | `referralProgram.emails.notifyCustomersAboutReward`
      | `referralProgram.premiumFeatures.referralProgram`,
      7
    >
  >;
}

/** @internal */
export function bulkGetReferralProgram(
  httpClient: HttpClient
): BulkGetReferralProgramSignature {
  return () =>
    universalBulkGetReferralProgram(
      // @ts-ignore
      { httpClient }
    );
}

interface BulkGetReferralProgramSignature {
  /**
   * Retrieves multiple referral programs for all metasites that the caller is the member of.
   *
   * Must be called with a [user identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#identity-types).
   */
  (): Promise<
    NonNullablePaths<
      BulkGetReferralProgramResponse,
      | `programInSites`
      | `programInSites.${number}.metaSiteId`
      | `programInSites.${number}.referralProgram.status`
      | `programInSites.${number}.referralProgram.referredFriendReward.type`
      | `programInSites.${number}.referralProgram.emails.notifyCustomersAboutReward`
      | `programInSites.${number}.referralProgram.premiumFeatures.referralProgram`,
      6
    >
  >;
}

export function updateReferralProgram(
  httpClient: HttpClient
): UpdateReferralProgramSignature {
  return (referralProgram: NonNullablePaths<ReferralProgram, `revision`, 2>) =>
    universalUpdateReferralProgram(
      referralProgram,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateReferralProgramSignature {
  /**
   * Updates a referral program. Supports partial updates.
   *
   * Revision number, which increments by 1 each time the referral program is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the referral program.
   * @param - Referral program to update. Include the latest `revision` for a successful update.
   */
  (referralProgram: NonNullablePaths<ReferralProgram, `revision`, 2>): Promise<
    NonNullablePaths<
      UpdateReferralProgramResponse,
      | `referralProgram.status`
      | `referralProgram.referredFriendReward.couponOptions.fixedAmountOptions.amount`
      | `referralProgram.referredFriendReward.couponOptions.percentageOptions.percentage`
      | `referralProgram.referredFriendReward.couponOptions.minimumSubtotal`
      | `referralProgram.referredFriendReward.couponOptions.scope.namespace`
      | `referralProgram.referredFriendReward.couponOptions.scope.group.name`
      | `referralProgram.referredFriendReward.couponOptions.name`
      | `referralProgram.referredFriendReward.couponOptions.discountType`
      | `referralProgram.referredFriendReward.loyaltyPointsOptions.amount`
      | `referralProgram.referredFriendReward.type`
      | `referralProgram.successfulReferralActions`
      | `referralProgram.emails.encourageToReferFriends`
      | `referralProgram.emails.notifyCustomersAboutReward`
      | `referralProgram.premiumFeatures.referralProgram`,
      7
    >
  >;
}

export function activateReferralProgram(
  httpClient: HttpClient
): ActivateReferralProgramSignature {
  return () =>
    universalActivateReferralProgram(
      // @ts-ignore
      { httpClient }
    );
}

interface ActivateReferralProgramSignature {
  /**
   * Activates the referral program, changing its status to `ACTIVE`.
   */
  (): Promise<
    NonNullablePaths<
      ActivateReferralProgramResponse,
      | `referralProgram.status`
      | `referralProgram.referredFriendReward.couponOptions.fixedAmountOptions.amount`
      | `referralProgram.referredFriendReward.couponOptions.percentageOptions.percentage`
      | `referralProgram.referredFriendReward.couponOptions.minimumSubtotal`
      | `referralProgram.referredFriendReward.couponOptions.scope.namespace`
      | `referralProgram.referredFriendReward.couponOptions.scope.group.name`
      | `referralProgram.referredFriendReward.couponOptions.name`
      | `referralProgram.referredFriendReward.couponOptions.discountType`
      | `referralProgram.referredFriendReward.loyaltyPointsOptions.amount`
      | `referralProgram.referredFriendReward.type`
      | `referralProgram.successfulReferralActions`
      | `referralProgram.emails.encourageToReferFriends`
      | `referralProgram.emails.notifyCustomersAboutReward`
      | `referralProgram.premiumFeatures.referralProgram`,
      7
    >
  >;
}

export function pauseReferralProgram(
  httpClient: HttpClient
): PauseReferralProgramSignature {
  return () =>
    universalPauseReferralProgram(
      // @ts-ignore
      { httpClient }
    );
}

interface PauseReferralProgramSignature {
  /**
   * Pauses the referral program, changing its status to `PAUSED`.
   */
  (): Promise<
    NonNullablePaths<
      PauseReferralProgramResponse,
      | `referralProgram.status`
      | `referralProgram.referredFriendReward.couponOptions.fixedAmountOptions.amount`
      | `referralProgram.referredFriendReward.couponOptions.percentageOptions.percentage`
      | `referralProgram.referredFriendReward.couponOptions.minimumSubtotal`
      | `referralProgram.referredFriendReward.couponOptions.scope.namespace`
      | `referralProgram.referredFriendReward.couponOptions.scope.group.name`
      | `referralProgram.referredFriendReward.couponOptions.name`
      | `referralProgram.referredFriendReward.couponOptions.discountType`
      | `referralProgram.referredFriendReward.loyaltyPointsOptions.amount`
      | `referralProgram.referredFriendReward.type`
      | `referralProgram.successfulReferralActions`
      | `referralProgram.emails.encourageToReferFriends`
      | `referralProgram.emails.notifyCustomersAboutReward`
      | `referralProgram.premiumFeatures.referralProgram`,
      7
    >
  >;
}

export function getAiSocialMediaPostsSuggestions(
  httpClient: HttpClient
): GetAiSocialMediaPostsSuggestionsSignature {
  return (options?: GetAiSocialMediaPostsSuggestionsOptions) =>
    universalGetAiSocialMediaPostsSuggestions(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAiSocialMediaPostsSuggestionsSignature {
  /**
   * Retrieves pre-generated AI social media post suggestions for promoting the referral program.
   *
   * This method returns a list of AI-generated social media post suggestions that site owners or members can use to promote the referral program. You can display these suggestions to allow referring customers to easily copy and share them on their preferred social media platforms.
   *
   * >**Note**: This method retrieves existing suggestions. To generate new ones,
   * use Generate AI Social Media Posts Suggestions.
   */
  (options?: GetAiSocialMediaPostsSuggestionsOptions): Promise<
    NonNullablePaths<
      GetAISocialMediaPostsSuggestionsResponse,
      `suggestions` | `suggestions.${number}.postContent`,
      4
    >
  >;
}

export function generateAiSocialMediaPostsSuggestions(
  httpClient: HttpClient
): GenerateAiSocialMediaPostsSuggestionsSignature {
  return (options?: GenerateAiSocialMediaPostsSuggestionsOptions) =>
    universalGenerateAiSocialMediaPostsSuggestions(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateAiSocialMediaPostsSuggestionsSignature {
  /**
   * Creates new AI-generated social media post suggestions for promoting the referral program.
   *
   * This method generates new AI-powered social media post suggestions for promoting the referral program. Use it to refresh content or create alternatives to existing suggestions.
   *
   * >**Note**: This method generates new suggestions each time it's called. To retrieve existing suggestions without generating new ones, use Get AI Social Media Posts Suggestions.
   */
  (options?: GenerateAiSocialMediaPostsSuggestionsOptions): Promise<
    NonNullablePaths<
      GenerateAISocialMediaPostsSuggestionsResponse,
      `suggestions` | `suggestions.${number}.postContent`,
      4
    >
  >;
}

export function getReferralProgramPremiumFeatures(
  httpClient: HttpClient
): GetReferralProgramPremiumFeaturesSignature {
  return () =>
    universalGetReferralProgramPremiumFeatures(
      // @ts-ignore
      { httpClient }
    );
}

interface GetReferralProgramPremiumFeaturesSignature {
  /**
   * Retrieves information about the enabled premium features for the referral program.
   */
  (): Promise<
    NonNullablePaths<
      GetReferralProgramPremiumFeaturesResponse,
      `referralProgram`,
      2
    >
  >;
}

export const onProgramUpdated = EventDefinition(
  'wix.loyalty.referral.v1.program_updated',
  true,
  (event: ProgramUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.referredFriendReward.couponOptions.minimumSubtotal',
            },
            {
              path: 'entity.referredFriendReward.couponOptions.fixedAmountOptions.amount',
            },
            {
              path: 'entity.referredFriendReward.couponOptions.percentageOptions.percentage',
            },
            {
              path: 'entity.referringCustomerReward.couponOptions.minimumSubtotal',
            },
            {
              path: 'entity.referringCustomerReward.couponOptions.fixedAmountOptions.amount',
            },
            {
              path: 'entity.referringCustomerReward.couponOptions.percentageOptions.percentage',
            },
          ],
        },
      ])
    )
)<ProgramUpdatedEnvelope>();

export {
  AISocialMediaPostSuggestion,
  AccountInfo,
  AccountInfoMetadata,
  Action,
  ActionEvent,
  ActivateReferralProgramRequest,
  ActivateReferralProgramResponse,
  App,
  BaseEventMetadata,
  BillingReference,
  BulkGetReferralProgramRequest,
  BulkGetReferralProgramResponse,
  CancellationDetails,
  ContractSwitchReason,
  ContractSwitchType,
  ContractSwitched,
  Coupon,
  CouponDiscountTypeOptionsOneOf,
  CouponScope,
  CouponScopeOrMinSubtotalOneOf,
  Cycle,
  CycleCycleSelectorOneOf,
  DiscountType,
  DomainEvent,
  DomainEventBodyOneOf,
  Emails,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FixedAmountDiscount,
  GenerateAISocialMediaPostsSuggestionsRequest,
  GenerateAISocialMediaPostsSuggestionsResponse,
  GenerateAiSocialMediaPostsSuggestionsOptions,
  GetAISocialMediaPostsSuggestionsRequest,
  GetAISocialMediaPostsSuggestionsResponse,
  GetAiSocialMediaPostsSuggestionsOptions,
  GetReferralProgramPremiumFeaturesRequest,
  GetReferralProgramPremiumFeaturesResponse,
  GetReferralProgramRequest,
  GetReferralProgramResponse,
  Group,
  HtmlSitePublished,
  IdentificationData,
  IdentificationDataIdOneOf,
  Initiator,
  Interval,
  IntervalUnit,
  LoyaltyPoints,
  MessageEnvelope,
  OneTime,
  Page,
  PauseReferralProgramRequest,
  PauseReferralProgramResponse,
  PercentageDiscount,
  PremiumFeatures,
  PriceIncreaseTrigger,
  ProductAdjustment,
  ProductPriceIncreaseData,
  ProgramInSite,
  ProgramStatus,
  ProgramUpdatedEnvelope,
  ProviderName,
  ReactivationData,
  ReactivationReasonEnum,
  RecurringChargeAttemptFailed,
  RecurringChargeSucceeded,
  ReferralAppInstallation,
  ReferralProgram,
  RefundType,
  RestoreInfo,
  Reward,
  RewardOptionsOneOf,
  RewardType,
  Subscription,
  SubscriptionAssigned,
  SubscriptionAutoRenewTurnedOff,
  SubscriptionAutoRenewTurnedOn,
  SubscriptionCancelled,
  SubscriptionCreated,
  SubscriptionEvent,
  SubscriptionEventEventOneOf,
  SubscriptionMigratedFromVista,
  SubscriptionNearEndOfPeriod,
  SubscriptionPendingChange,
  SubscriptionStatus,
  SubscriptionTransferred,
  SubscriptionUnassigned,
  SubscriptionUpdated,
  SubscriptionVistaMigrationReverted,
  Type,
  UnassignReason,
  UpdateReferralProgramRequest,
  UpdateReferralProgramResponse,
  WebhookIdentityType,
} from './loyalty-referral-v1-program-programs.universal.js';
